import { Autocomplete, TextField } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface DashboardBranchOption {
  branchId: number;
  name: string;
}

export interface DashboardEmployeeOption {
  userId: number;
  name: string;
}

type BranchFilterOption = DashboardBranchOption | { branchId: null; name: string };
type EmployeeFilterOption = DashboardEmployeeOption | { userId: null; name: string };

interface DashboardScopeFilterProps {
  selectedBranch: number | null;
  selectedEmployee: number | null;
  setSelectedBranch: (branchId: number | null) => void;
  setSelectedEmployee: (employeeId: number | null) => void;
  branches: DashboardBranchOption[];
  employees: DashboardEmployeeOption[];
}

const autocompleteSx = {
  width: "100%",
  "& .MuiInputBase-root": {
    fontFamily: "Montserrat",
    fontSize: "0.8rem",
    backgroundColor: "#2D2F32",
    color: "#fff",
    borderRadius: "999px",
    paddingLeft: "12px !important",
    paddingRight: "8px !important",
    minHeight: "32px",
  },
  "& .MuiInputBase-input": {
    padding: "4px 0 !important",
    color: "#fff",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSvgIcon-root": {
    color: "#EF0BB8",
  },
  "& .MuiAutocomplete-clearIndicator": {
    color: "rgba(255,255,255,0.7)",
  },
};

const paperSx = {
  backgroundColor: "#2D2F32",
  color: "#fff",
  fontFamily: "Montserrat",
  "& .MuiAutocomplete-option": {
    fontSize: "0.85rem",
    fontFamily: "Montserrat",
  },
  "& .MuiAutocomplete-noOptions": {
    fontFamily: "Montserrat",
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.7)",
  },
};

export function DashboardScopeFilter({
  selectedBranch,
  setSelectedBranch,
  selectedEmployee,
  setSelectedEmployee,
  branches,
  employees,
}: DashboardScopeFilterProps) {
  const { t } = useTranslation();

  const branchOptions = useMemo<BranchFilterOption[]>(
    () => [
      { branchId: null, name: t("dashboard.filters.all-branches", "Todas las sucursales") },
      ...branches,
    ],
    [branches, t]
  );

  const employeeOptions = useMemo<EmployeeFilterOption[]>(
    () => [
      { userId: null, name: t("dashboard.filters.all-employees", "Todos los empleados") },
      ...employees,
    ],
    [employees, t]
  );

  const selectedBranchOption =
    branchOptions.find((option) => option.branchId === selectedBranch) ?? branchOptions[0];
  const selectedEmployeeOption =
    employeeOptions.find((option) => option.userId === selectedEmployee) ?? employeeOptions[0];

  return (
    <div className="flex flex-col gap-2 w-full">
      <span className="text-[#898989] text-xs font-semibold">
        {t("messages.filter.title")}
      </span>
      <div className="flex flex-col gap-2 w-full">
        <Autocomplete
          options={branchOptions}
          value={selectedBranchOption}
          onChange={(_event, value) => {
            setSelectedBranch(value?.branchId ?? null);
            setSelectedEmployee(null);
          }}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.branchId === value.branchId}
          filterOptions={(options, state) => {
            const query = state.inputValue.toLowerCase().trim();
            if (!query) return options;
            return options.filter((option) =>
              option.name.toLowerCase().includes(query)
            );
          }}
          noOptionsText={t("dashboard.filters.no-results", "Sin resultados")}
          sx={autocompleteSx}
          slotProps={{
            paper: { sx: paperSx },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("messages.filter.branch")}
              size="small"
            />
          )}
        />

        <Autocomplete
          options={employeeOptions}
          value={selectedEmployeeOption}
          onChange={(_event, value) => {
            setSelectedEmployee(value?.userId ?? null);
          }}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.userId === value.userId}
          filterOptions={(options, state) => {
            const query = state.inputValue.toLowerCase().trim();
            if (!query) return options;
            return options.filter((option) =>
              option.name.toLowerCase().includes(query)
            );
          }}
          noOptionsText={t("dashboard.filters.no-results", "Sin resultados")}
          sx={autocompleteSx}
          slotProps={{
            paper: { sx: paperSx },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("messages.filter.employee")}
              size="small"
            />
          )}
        />
      </div>
    </div>
  );
}
